import React from 'react';
import { createRoot } from 'react-dom/client';

// Import shared quiz components
import { StepByStepPreview } from '../../../src/components/StepByStepPreview';
import { Theme, Block } from '../../../src/types';

type QuizConfig = {
  title?: string;
  blocks?: Block[];
  theme?: Theme;
  projectId?: string;
  slug?: string;
};

async function getQuizConfig({ slug, pageId }: { slug?: string; pageId?: string; }): Promise<QuizConfig> {
  const qs = new URLSearchParams();
  if (slug) qs.set('slug', slug);
  if (pageId) qs.set('pageId', pageId);
  
  const res = await fetch(`https://quiz-funnel-saas.vercel.app/api/embed/config?${qs.toString()}`, { 
    credentials: 'omit',
    headers: {
      'Accept': 'application/json',
    }
  });
  if (!res.ok) throw new Error('Failed to load quiz config');
  return res.json();
}

export async function getShopifyProduct(handle: string) {
  const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Product not found: ${handle}`);
  return res.json();
}

export async function addToCart(variantId: number, qty = 1, properties?: Record<string,string>) {
  const payload = { items: [{ id: variantId, quantity: qty, properties }] };
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(payload)
  });
  if (!res.ok) throw new Error('Add to cart failed');
  return res.json();
}

export async function setCartAttributes(attrs: Record<string, string>) {
  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ attributes: attrs })
  });
  if (!res.ok) throw new Error('Cart attribute update failed');
  return res.json();
}

function openCartOrRedirect(to: 'cart'|'checkout' = 'cart') {
  try { 
    document.dispatchEvent(new CustomEvent('cart:refresh')); 
  } catch {}
  try { 
    document.dispatchEvent(new CustomEvent('cart:open')); 
  } catch {}
  setTimeout(() => { 
    window.location.href = to === 'checkout' ? '/checkout' : '/cart'; 
  }, 400);
}

export async function bootstrap(rootId = 'quiz-root') {
  const rootEl = document.getElementById(rootId);
  if (!rootEl) return;

  // Make quiz full-bleed by spanning all grid columns (for grid-based Shopify themes)
  rootEl.style.gridColumn = '1 / -1';

  const slug = rootEl.dataset.slug || undefined;
  const pageId = rootEl.dataset.pageId || undefined;
  const layout = rootEl.dataset.layout || 'full-page'; // 'full-page' or 'inline'
  
  // Apply layout class to root element
  if (layout === 'inline') {
    rootEl.classList.add('quiz-layout-inline');
  } else {
    rootEl.classList.add('quiz-layout-full-page');
  }

  // UTM + session tracking
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = Object.fromEntries(Array.from(params.entries()).filter(([k]) => k.startsWith('utm_')));
    const sid = (() => {
      const k = 'qf_session_id';
      const v = localStorage.getItem(k) || crypto.randomUUID();
      localStorage.setItem(k, v);
      return v;
    })();
    
    // Track session start
    if (slug) {
      fetch('https://quiz-funnel-saas.vercel.app/api/events/ingest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'session_start',
          slug,
          sessionId: sid,
          utm,
          referrer: document.referrer,
          timestamp: new Date().toISOString()
        })
      }).catch(() => {}); // Silent fail for analytics
    }
  } catch {}

  const container = document.createElement('div');
  container.className = 'qz';
  rootEl.appendChild(container);

  // Show loading spinner while fetching config
  container.innerHTML = `
    <div class="qz-card qz-loading"></div>
  `;

  try {
    const config = await getQuizConfig({ slug, pageId });

    // Ensure cart attribution is set early
    if (config.projectId) {
      await setCartAttributes({ quiz_funnel_project_id: String(config.projectId) });
    }

    const root = createRoot(container);
    root.render(
      <StepByStepPreview
        blocks={config.blocks || []}
        mode="desktop"
        onModeChange={() => {}}
        onStepView={(stepId) => {
          // Track step views
          fetch('https://quiz-funnel-saas.vercel.app/api/events/ingest', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'step_view',
              stepId,
              projectId: config.projectId,
              sessionId: localStorage.getItem('qf_session_id'),
              timestamp: new Date().toISOString()
            })
          }).catch(() => {});
        }}
        onStepComplete={(stepId) => {
          // Track step completion
          fetch('https://quiz-funnel-saas.vercel.app/api/events/ingest', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'step_complete',
              stepId,
              projectId: config.projectId,
              sessionId: localStorage.getItem('qf_session_id'),
              timestamp: new Date().toISOString()
            })
          }).catch(() => {});
        }}
        onCtaClick={(stepId, metadata) => {
          // Track CTA clicks
          fetch('https://quiz-funnel-saas.vercel.app/api/events/ingest', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'cta_click',
              stepId,
              projectId: config.projectId,
              sessionId: localStorage.getItem('qf_session_id'),
              metadata,
              timestamp: new Date().toISOString()
            })
          }).catch(() => {});
        }}
        onAnswerEvent={(block, answer) => {
          // Track answers
          fetch('https://quiz-funnel-saas.vercel.app/api/events/ingest', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'answer',
              stepId: block.id,
              questionId: block.id,
              questionType: block.type,
              answer,
              projectId: config.projectId,
              sessionId: localStorage.getItem('qf_session_id'),
              timestamp: new Date().toISOString()
            })
          }).catch(() => {});
        }}
        isShopifyProxy={true}
        shopDomain={window.location.hostname}
        projectId={config.projectId}
        showHeader={false}
        theme={config.theme}
        isEmbedded={true}
        // Shopify-specific props
        getProduct={getShopifyProduct}
        addToCart={(variantId: number, qty = 1) => addToCart(variantId, qty, { QuizProject: String(config.projectId || '') })}
      />
    );
  } catch (e) {
    container.innerHTML = `
      <div class="qz-card qz-error">
        <h3>We're having trouble loading this quiz.</h3>
        <p>Please refresh or try again later.</p>
        <p><small>Error: ${e instanceof Error ? e.message : 'Unknown error'}</small></p>
      </div>
    `;
    console.error('Quiz embed error:', e);
  }
}
